import { useState } from 'react';
import { Languages, Loader2, Pause, Play } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils/cn';
import { useTranscribeAudio } from '../api';
import {
  type PromptRow,
  type TranscribeResponse,
  type TtsGender,
} from '../types';

interface Props {
  prompt: PromptRow | null;
  onOpenChange: (open: boolean) => void;
}

export function TranscribeDialog({ prompt, onOpenChange }: Props) {
  const transcribe = useTranscribeAudio();
  const [gender, setGender] = useState<TtsGender>('MALE');
  const [result, setResult] = useState<TranscribeResponse | null>(null);

  const audioUrl = prompt?.promptUrl;

  const [playing, setPlaying] = useState<'src' | 'translated' | null>(null);

  const handleTranscribe = () => {
    if (!audioUrl) return toast.error('No audio URL on this prompt.');
    transcribe.mutate(
      { audioUrl, gender },
      {
        onSuccess: (res) => {
          setResult(res);
          if (res.status === 'fail') toast.error(res.msg ?? 'Could not transcribe.');
        },
        onError: () => toast.error('Could not transcribe.'),
      },
    );
  };

  const togglePlay = (which: 'src' | 'translated', url?: string) => {
    if (!url) return;
    if (playing === which) {
      setPlaying(null);
      return;
    }
    setPlaying(which);
    const a = new Audio(url);
    a.addEventListener('ended', () => setPlaying(null));
    void a.play();
  };

  const reset = () => {
    setResult(null);
    setPlaying(null);
  };

  return (
    <Dialog
      open={Boolean(prompt)}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Languages className="h-4 w-4" />
            Transcribe to Hindi
          </DialogTitle>
          <DialogDescription>
            Send{' '}
            <span className="font-medium text-foreground">
              {prompt?.fileName}
            </span>{' '}
            to the external transcription service. You'll get back the detected
            language, the original transcript, and a Hindi translation with a
            new audio file.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Voice for the Hindi rendering</Label>
            <Select
              value={gender}
              onValueChange={(v) => setGender(v as TtsGender)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MALE">Male</SelectItem>
                <SelectItem value="FEMALE">Female</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {audioUrl && (
            <div className="flex items-center gap-2 rounded-md border border-border p-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={cn(
                  'h-8 w-8 rounded-full',
                  playing === 'src' && 'border-primary text-primary',
                )}
                onClick={() => togglePlay('src', audioUrl)}
                aria-label="Play original audio"
              >
                {playing === 'src' ? (
                  <Pause className="h-3.5 w-3.5" />
                ) : (
                  <Play className="h-3.5 w-3.5" />
                )}
              </Button>
              <span className="text-xs text-muted-foreground">
                Original prompt
              </span>
            </div>
          )}

          {result?.status === 'success' && (
            <div className="space-y-3 rounded-md border border-emerald-200 bg-emerald-50/40 p-3">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-900">
                  Detected language
                </div>
                <div className="text-sm font-medium text-foreground">
                  {result.detectedLanguage}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-900">
                  Transcript
                </div>
                <div className="text-sm text-foreground">
                  {result.transcript}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-900">
                  Hindi translation
                </div>
                <div className="text-sm text-foreground">
                  {result.hindiText}
                </div>
              </div>
              {result.downloadUrl && (
                <div className="flex items-center gap-2 border-t border-emerald-200/70 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className={cn(
                      'h-8 w-8 rounded-full',
                      playing === 'translated' && 'border-primary text-primary',
                    )}
                    onClick={() =>
                      togglePlay('translated', result.downloadUrl)
                    }
                    aria-label="Play translated audio"
                  >
                    {playing === 'translated' ? (
                      <Pause className="h-3.5 w-3.5" />
                    ) : (
                      <Play className="h-3.5 w-3.5" />
                    )}
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Hindi rendering
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            onClick={handleTranscribe}
            disabled={transcribe.isPending || !audioUrl}
          >
            {transcribe.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            Transcribe
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
